import { useState } from 'react';
import { Eye, EyeOff, Copy, Download, Shield, AlertTriangle, ExternalLink, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { copyToClipboard } from '../../utils/clipboard';
import { getSolanaClusterParam } from '@/utils/solanaExplorer';

interface WalletSettingsProps {
  walletAddress?: string;
  did?: string;
  authMethod?: string;
}

export function WalletSettings({ walletAddress, did, authMethod = 'Social Login' }: WalletSettingsProps) {
  const { t } = useTranslation();
  const displayWalletAddress = walletAddress || t('settings.walletIdentity.walletNotCreatedYet');
  const displayDid = did || `did:petso:${walletAddress ? walletAddress.slice(0, 16) + '...' : 'pending'}`;
  const hasWallet = !!walletAddress;
  const [showWalletDetails, setShowWalletDetails] = useState(false);
  const [showSeedPhrase, setShowSeedPhrase] = useState(false);
  const [confirmExport, setConfirmExport] = useState(false);

  // Mock seed phrase (in real implementation, this would be encrypted and never stored)
  const seedPhrase = [
    'abandon', 'ability', 'able', 'about', 'above', 'absent',
    'absorb', 'abstract', 'absurd', 'abuse', 'access', 'accident'
  ];

  const handleCopyAddress = async () => {
    if (!walletAddress) {
      toast.error(t('settings.walletIdentity.noWalletToCopy'));
      return;
    }
    await copyToClipboard(walletAddress);
    toast.success(t('settings.walletIdentity.walletAddressCopied'));
  };

  const handleCopyDID = async () => {
    if (!walletAddress) {
      toast.error(t('settings.walletIdentity.noDidToCopy'));
      return;
    }
    await copyToClipboard(displayDid);
    toast.success(t('settings.walletIdentity.didCopied'));
  };

  const handleExportWallet = () => {
    // In real implementation, this would securely export the wallet
    toast.success(t('settings.walletIdentity.walletExportInitiated'));
    setConfirmExport(false);
  };

  const handleViewOnExplorer = () => {
    if (!walletAddress) {
      toast.error(t('settings.walletIdentity.noWalletToView'));
      return;
    }
    window.open(`https://solscan.io/account/${walletAddress}${getSolanaClusterParam()}`, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-2xl mb-2 dark:text-white">
          {t('settings.walletIdentity.title')}
        </h3>
        <p className="text-gray-700 dark:text-white/70">
          {t('settings.walletIdentity.subtitle')}
        </p>
      </div>

      {/* Security Badge */}
      {hasWallet ? (
        <div className="p-4 rounded-xl flex items-start gap-3 bg-green-100 dark:bg-green-900/20 border border-green-500 dark:border-green-500/30">
          <Shield className="w-5 h-5 flex-shrink-0 mt-0.5 text-green-600 dark:text-green-400" />
          <div>
            <div className="text-sm text-green-900 dark:text-green-300 font-medium">
              {t('settings.walletIdentity.yourWalletSecured')}
            </div>
            <div className="text-xs text-green-700 dark:text-green-400">
              {t('settings.walletIdentity.authenticatedVia', { method: authMethod.charAt(0).toUpperCase() + authMethod.slice(1) })} • {t('settings.walletIdentity.encryptedWithMPC')}
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4 rounded-xl flex items-start gap-3 bg-amber-100 dark:bg-amber-900/20 border border-amber-500 dark:border-amber-500/30">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
          <div>
            <div className="text-sm text-amber-900 dark:text-amber-300 font-medium">
              {t('settings.walletIdentity.walletPendingCreation')}
            </div>
            <div className="text-xs text-amber-700 dark:text-amber-400">
              {t('settings.walletIdentity.walletWillBeCreated')}
            </div>
          </div>
        </div>
      )}

      {/* Basic Info (Always Visible) */}
      <div className="space-y-4">
        {/* DID */}
        <div className="p-4 rounded-xl bg-gray-100 dark:bg-[#16151D] border border-gray-200 dark:border-white/8">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm text-gray-700 dark:text-white/90 font-medium">
              {t('settings.walletIdentity.decentralizedIdentity')}
            </label>
            <button
              onClick={handleCopyDID}
              className="p-2 hover:bg-white dark:hover:bg-white/5 rounded-lg transition-colors"
              title={t('settings.walletIdentity.copyDID')}
            >
              <Copy className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </button>
          </div>
          <div className="px-3 py-2 rounded-lg font-mono break-all text-sm bg-white dark:bg-[#0F0E16] text-purple-900 dark:text-purple-300">
            {displayDid}
          </div>
        </div>

        {/* Petso Secure Account */}
        <div className="p-4 rounded-xl bg-gray-100 dark:bg-[#16151D] border border-gray-200 dark:border-white/8">
          <div className="flex items-center justify-between mb-3">
            <div>
              <label className="text-sm text-gray-700 dark:text-white/90 font-medium">
                {t('settings.walletIdentity.petsoSecureAccount')}
              </label>
              <p className="text-xs text-gray-500 dark:text-white/70">
                {t('settings.walletIdentity.blockchainWalletAbstracted')}
              </p>
            </div>
            <button
              onClick={() => setShowWalletDetails(!showWalletDetails)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors hover:bg-white dark:hover:bg-white/5"
              style={{ fontSize: '0.875rem', color: '#594BFF', fontFamily: 'Inter, sans-serif', fontWeight: 500 }}
            >
              {showWalletDetails ? (
                <>
                  <EyeOff className="w-4 h-4" />
                  {t('settings.walletIdentity.hide')}
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  {t('settings.walletIdentity.viewDetails')}
                </>
              )}
            </button>
          </div>

          {!showWalletDetails && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-white dark:bg-[#0F0E16]">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              <span className="text-sm text-purple-900 dark:text-purple-300">
                {t('settings.walletIdentity.activeAndSecured')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Advanced Wallet Details (Toggle) */}
      {showWalletDetails && (
        <div 
          className="p-6 rounded-xl space-y-4 bg-white dark:bg-[#16151D] border-2 border-purple-500 dark:border-purple-500/50"
          style={{ boxShadow: '0 8px 24px rgba(89, 75, 255, 0.08)' }}
        >
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            <h4 className="text-base text-gray-900 dark:text-white" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>
              {t('settings.walletIdentity.advancedWalletInfo')}
            </h4>
          </div>

          {/* Wallet Address */}
          <div>
            <label className="block text-sm text-gray-700 dark:text-white/90 font-medium mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
              {t('settings.walletIdentity.solanaWalletAddress')}
            </label>
            <div className="flex gap-2">
              <div 
                className={`flex-1 px-3 py-2 rounded-lg font-mono break-all text-sm bg-gray-50 dark:bg-[#0F0E16] ${hasWallet ? 'text-purple-900 dark:text-purple-300' : 'text-gray-500 dark:text-gray-400 italic'}`}
                style={{ fontFamily: 'monospace' }}
              >
                {displayWalletAddress}
              </div>
              {hasWallet && (
                <button
                  onClick={handleCopyAddress}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg transition-colors flex-shrink-0"
                  title={t('settings.walletIdentity.copyAddress')}
                >
                  <Copy className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </button>
              )}
            </div>
          </div>

          {/* View on Explorer */}
          <button
            onClick={handleViewOnExplorer}
            className="w-full p-3 rounded-lg flex items-center justify-center gap-2 transition-colors hover:opacity-90 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 font-medium"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <ExternalLink className="w-4 h-4" />
            {t('settings.walletIdentity.viewOnSolscan')}
          </button>

          {/* Seed Phrase Section */}
          <div className="pt-4 border-t border-gray-200 dark:border-white/8">
            <div 
              className="p-4 rounded-lg mb-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-400 dark:border-amber-500/30"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                <div>
                  <div className="text-sm text-amber-900 dark:text-amber-300 font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {t('settings.walletIdentity.securityWarning')}
                  </div>
                  <div className="text-xs text-amber-800 dark:text-amber-400" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {t('settings.walletIdentity.neverShareSeedPhrase')}
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowSeedPhrase(!showSeedPhrase)}
              className="w-full p-3 rounded-lg flex items-center justify-center gap-2 transition-colors hover:opacity-90 bg-gray-50 dark:bg-[#0F0E16] text-gray-700 dark:text-white/70 font-medium border border-gray-300 dark:border-white/8"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {showSeedPhrase ? (
                <>
                  <EyeOff className="w-4 h-4" />
                  {t('settings.walletIdentity.hideSeedPhrase')}
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  {t('settings.walletIdentity.revealSeedPhrase')}
                </>
              )}
            </button>

            {showSeedPhrase && (
              <div className="mt-3 p-4 rounded-lg bg-gray-50 dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8">
                <div className="grid grid-cols-3 gap-2">
                  {seedPhrase.map((word, index) => (
                    <div 
                      key={index}
                      className="px-3 py-2 rounded-lg bg-white dark:bg-[#16151D] border border-gray-200 dark:border-white/8"
                    >
                      <span className="text-xs text-gray-500 dark:text-white/50" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {index + 1}.
                      </span>
                      {' '}
                      <span className="text-sm text-purple-900 dark:text-purple-300 font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {word}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Export Wallet */}
          <div className="pt-4 border-t border-gray-200 dark:border-white/8">
            {!confirmExport ? (
              <button
                onClick={() => setConfirmExport(true)}
                className="w-full p-3 rounded-lg flex items-center justify-center gap-2 transition-colors hover:opacity-90 bg-purple-600 dark:bg-purple-500 text-white font-medium"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <Download className="w-4 h-4" />
                {t('settings.walletIdentity.exportWallet')}
              </button>
            ) : (
              <div className="space-y-3">
                <div 
                  className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-400 dark:border-red-500/30"
                >
                  <p className="text-sm text-red-900 dark:text-red-300" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {t('settings.walletIdentity.exportConfirmMessage')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setConfirmExport(false)}
                    className="flex-1 py-2 rounded-lg transition-colors bg-gray-100 dark:bg-[#0F0E16] text-gray-700 dark:text-white/70 font-medium"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleExportWallet}
                    className="flex-1 py-2 rounded-lg transition-colors hover:opacity-90 bg-red-600 dark:bg-red-500 text-white font-medium"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('settings.walletIdentity.confirmExport')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recovery Information */}
      <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-500/30">
        <h4 className="text-sm text-blue-900 dark:text-blue-300 font-semibold mb-2">
          {t('settings.walletIdentity.accountRecovery')}
        </h4>
        <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
          {t('settings.walletIdentity.accountRecoveredThrough')}
        </p>
        <ul className="space-y-1">
          <li className="text-sm text-blue-800 dark:text-blue-200">
            • {t('settings.walletIdentity.reVerificationVia', { method: authMethod })}
          </li>
          <li className="text-sm text-blue-800 dark:text-blue-200">
            • {t('settings.walletIdentity.passkeyReEnrollment')}
          </li>
          <li className="text-sm text-blue-800 dark:text-blue-200">
            • {t('settings.walletIdentity.seedPhraseIfExported')}
          </li>
        </ul>
      </div>
    </div>
  );
}